import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash2, Upload, Link2 } from "lucide-react";

export default function ProposalVideoManager({ proposalId, video, onChange }) {
  const [url, setUrl] = useState("");
  const [heading, setHeading] = useState(video?.heading || "");
  const [busy, setBusy] = useState(false);

  const saveLink = async () => {
    if (!url.trim()) return toast.error("Paste a Loom, YouTube or Vimeo link");
    setBusy(true);
    try {
      const { data } = await api.put(`/proposals/${proposalId}/video`, { type: "link", url: url.trim(), heading });
      toast.success("Video added"); setUrl(""); setHeading(data.heading); onChange?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add video"); }
    finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) return toast.error("Video must be 100MB or smaller — paste a link for longer clips");
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/proposals/${proposalId}/video/upload?heading=${encodeURIComponent(heading || "")}`, fd);
      toast.success("Video uploaded"); setHeading(data.heading); onChange?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const saveHeading = async () => {
    if (!video) return;
    try {
      const { data } = await api.put(`/proposals/${proposalId}/video`, { type: video.type, url: video.url, heading });
      toast.success("Heading saved"); onChange?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  const remove = async () => {
    await api.delete(`/proposals/${proposalId}/video`);
    toast.success("Video removed"); setHeading(""); onChange?.(null);
  };

  if (!proposalId) return <p className="text-sm text-cass-slate">Save the proposal first, then you can add a personal video.</p>;

  return (
    <div className="space-y-3" data-testid="video-manager">
      <div>
        <label className="text-xs text-cass-slate">Heading shown above the video</label>
        <div className="flex gap-2 mt-1">
          <Input value={heading} onChange={(e) => setHeading(e.target.value)} placeholder="A quick message about your proposal" data-testid="video-heading-input" />
          {video && <Button variant="outline" onClick={saveHeading} data-testid="video-heading-save">Save</Button>}
        </div>
      </div>
      {video ? (
        <div className="flex items-center justify-between gap-2 border border-border rounded-md p-3" data-testid="video-current">
          <div className="min-w-0 text-sm">
            <div className="text-cass-navy font-medium capitalize">{video.provider} video attached</div>
            <div className="text-xs text-cass-slate truncate">{video.url}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={remove} data-testid="video-remove"><Trash2 className="w-4 h-4 mr-1" />Remove</Button>
        </div>
      ) : (
        <>
          <div>
            <label className="text-xs text-cass-slate flex items-center gap-1"><Link2 className="w-3 h-3" />Paste a video link (Loom, YouTube or Vimeo)</label>
            <div className="flex gap-2 mt-1">
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.loom.com/share/…" data-testid="video-url-input" />
              <Button className="bg-cass-navy text-white shrink-0" disabled={busy} onClick={saveLink} data-testid="video-link-save">Add</Button>
            </div>
          </div>
          <div className="text-xs text-cass-slate">or</div>
          <label className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded border border-border cursor-pointer hover:bg-cass-bone w-fit" data-testid="video-upload-label">
            <Upload className="w-4 h-4" />{busy ? "Uploading…" : "Upload a video file (MP4/MOV/WEBM, ≤100MB)"}
            <input type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="video-upload-input" />
          </label>
        </>
      )}
    </div>
  );
}
